import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { cadastralApi } from "@/services/api-v2.service";
import {
  Database,
  RefreshCw,
  Settings,
  Trash2,
  Upload
} from "lucide-react";

interface CacheStats {
  totalUnits: number;
  cacheSize: string;
  lastUpdated: string;
  hitRate: number;
  sources: {
    openstreetmap: number;
    manual: number;
  };
  adminLevels: {
    [level: number]: number;
  };
}

interface CacheSettings {
  useCache: boolean;
  cacheTtlHours: number;
  autoUpdate: boolean;
  dataSources: string[];
  adminLevels: number[];
  maxCacheSizeMb: number;
  cleanupOldData: boolean;
}

export default function CadastralCacheManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [, setIsImportOpen] = useState(false);
  const [settings, setSettings] = useState<CacheSettings>({
    useCache: true,
    cacheTtlHours: 24,
    autoUpdate: true,
    dataSources: ["openstreetmap", "manual"],
    adminLevels: [4, 5, 6],
    maxCacheSizeMb: 100,
    cleanupOldData: true
  });

  // Получение статистики кэша
  const { data: cacheStats, isLoading: isLoadingStats } = useQuery<CacheStats>({
    queryKey: ['cadastral', 'cache', 'stats'],
    queryFn: async () => {
      const stats = await cadastralApi.getCacheStatistics();
      return {
        totalUnits: stats.total || 0,
        cacheSize: `${stats.estimatedSizeMb.toFixed(2)} MB`,
        lastUpdated: stats.newestUpdate || stats.oldestUpdate || new Date().toISOString(),
        hitRate: 0, // TODO: Добавить расчет hit rate
        sources: {
          openstreetmap: stats.bySource?.openstreetmap || 0,
          manual: stats.bySource?.manual || 0,
        },
        adminLevels: stats.byAdminLevel || {},
      };
    },
    refetchInterval: 30000, // Обновляем каждые 30 секунд
  });

  // Получение настроек кэша
  const { data: currentSettings } = useQuery<CacheSettings>({
    queryKey: ['cadastral', 'cache', 'settings'],
    queryFn: async () => {
      const settings = await cadastralApi.getCacheSettings();
      return {
        useCache: settings.useCache ?? true,
        cacheTtlHours: settings.cacheTtlHours ?? 24,
        autoUpdate: settings.autoUpdate ?? true,
        dataSources: settings.dataSources || ["openstreetmap", "manual"],
        adminLevels: settings.adminLevels || [4, 5, 6],
        maxCacheSizeMb: settings.maxCacheSizeMb ?? 100,
        cleanupOldData: settings.cleanupOldData ?? true,
      };
    },
  });

  // Обновление локальных настроек при получении данных с сервера
  React.useEffect(() => {
    if (currentSettings) {
      setSettings(currentSettings);
    }
  }, [currentSettings]);

  // Мутация для обновления кэша
  const refreshCacheMutation = useMutation({
    mutationFn: async () => {
      return await cadastralApi.refreshCache();
    },
    onSuccess: () => {
      toast({
        title: "Кэш обновлен",
        description: "Кэш кадастровых данных успешно обновлен"
      });
      queryClient.invalidateQueries({ queryKey: ['cadastral', 'cache', 'stats'] });
    },
    onError: () => {
      toast({
        title: "Ошибка",
        description: "Не удалось обновить кэш",
        variant: "destructive"
      });
    }
  });

  // Мутация для очистки кэша
  const clearCacheMutation = useMutation({
    mutationFn: async () => {
      // TODO: Проверить, есть ли метод clearCache в cadastralApi
      const token = localStorage.getItem("access_token");
      const response = await fetch('/api/v1/cadastral/cache/clear', {
        method: 'POST',
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Failed to clear cache");
      return await response.json();
    },
    onSuccess: () => {
      toast({
        title: "Кэш очищен",
        description: "Все кэшированные данные удалены"
      });
      queryClient.invalidateQueries({ queryKey: ['cadastral', 'cache', 'stats'] });
    },
    onError: () => {
      toast({
        title: "Ошибка",
        description: "Не удалось очистить кэш",
        variant: "destructive"
      });
    }
  });

  // Мутация для сохранения настроек
  const saveSettingsMutation = useMutation({
    mutationFn: async (newSettings: CacheSettings) => {
      const updateData = {
        useCache: newSettings.useCache,
        cacheTtlHours: newSettings.cacheTtlHours,
        autoUpdate: newSettings.autoUpdate,
        dataSources: newSettings.dataSources,
        adminLevels: newSettings.adminLevels,
        maxCacheSizeMb: newSettings.maxCacheSizeMb,
        cleanupOldData: newSettings.cleanupOldData,
      };
      return await cadastralApi.updateCacheSettings(updateData);
    },
    onSuccess: () => {
      toast({
        title: "Настройки сохранены",
        description: "Настройки кэша успешно обновлены"
      });
      setIsSettingsOpen(false);
      queryClient.invalidateQueries({ queryKey: ['cadastral', 'cache', 'settings'] });
    },
    onError: () => {
      toast({
        title: "Ошибка",
        description: "Не удалось сохранить настройки",
        variant: "destructive"
      });
    }
  });

  const handleSaveSettings = () => {
    saveSettingsMutation.mutate(settings);
  };

  // Функция для форматирования даты в человекочитаемый формат
  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Не указано';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Если невалидная дата, вернуть как есть
      
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      // Если меньше минуты назад
      if (diffMins < 1) return 'Только что';
      
      // Если меньше часа назад
      if (diffMins < 60) {
        const mins = diffMins === 1 ? 'минуту' : diffMins < 5 ? 'минуты' : 'минут';
        return `${diffMins} ${mins} назад`;
      }
      
      // Если меньше суток назад
      if (diffHours < 24) {
        const hours = diffHours === 1 ? 'час' : diffHours < 5 ? 'часа' : 'часов';
        return `${diffHours} ${hours} назад`;
      }
      
      // Если меньше недели назад
      if (diffDays < 7) {
        const days = diffDays === 1 ? 'день' : diffDays < 5 ? 'дня' : 'дней';
        return `${diffDays} ${days} назад`;
      }
      
      // Иначе показываем полную дату и время
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="space-y-6 mb-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Управление кэшем кадастровых данных
              </CardTitle>
              <CardDescription>
                Настройка и мониторинг кэша российских административных единиц
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsSettingsOpen(true)}
              >
                <Settings className="h-4 w-4 mr-1" />
                Настройки
              </Button>
              <Button
                variant="outline" 
                size="sm"
                onClick={() => refreshCacheMutation.mutate()}
                disabled={refreshCacheMutation.isPending}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${refreshCacheMutation.isPending ? 'animate-spin' : ''}`} />
                Обновить
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingStats ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : cacheStats ? (
            <div className="space-y-4">
              {/* Статистика кэша */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="text-sm font-medium text-muted-foreground">Всего единиц</div>
                  <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{cacheStats.totalUnits}</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="text-sm font-medium text-muted-foreground">Размер кэша</div>
                  <div className="text-xl font-bold text-green-600 dark:text-green-400">{cacheStats.cacheSize}</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="text-sm font-medium text-muted-foreground">Процент попаданий</div>
                  <div className="text-xl font-bold text-purple-600 dark:text-purple-400">{cacheStats.hitRate}%</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="text-sm font-medium text-muted-foreground">Последнее обновление</div>
                  <div className="text-sm font-bold text-orange-600 dark:text-orange-400">{formatDateTime(cacheStats.lastUpdated)}</div>
                </div>
              </div>

              {/* Распределение по источникам */}
              <div>
                <h4 className="font-medium mb-2">Источники данных</h4>
                <div className="flex gap-2">
                  <Badge variant="secondary">
                    OpenStreetMap: {cacheStats.sources.openstreetmap}
                  </Badge>
                  <Badge variant="secondary">
                    Ручной ввод: {cacheStats.sources.manual}
                  </Badge>
                </div>
              </div>

              {/* Распределение по административным уровням */}
              <div>
                <h4 className="font-medium mb-2">Административные уровни</h4>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(cacheStats.adminLevels).map(([level, count]) => (
                    <Badge key={level} variant="outline">
                      Уровень {level}: {count} единиц
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Действия с кэшем */}
              <div className="flex gap-2 pt-4 border-t border-border">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => clearCacheMutation.mutate()}
                  disabled={clearCacheMutation.isPending}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Очистить кэш
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsImportOpen(true)}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Импорт данных
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Нет данных о кэше
            </div>
          )}
        </CardContent>
      </Card>

      {/* Диалог настроек кэша */}
      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Настройки кэша кадастровых данных</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Использовать кэш</Label>
                <p className="text-xs text-muted-foreground">Включить кэширование данных</p>
              </div>
              <Switch
                checked={settings.useCache}
                onCheckedChange={(checked) => setSettings({ ...settings, useCache: checked })}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Время жизни кэша (часы)</Label>
              <Input
                type="number"
                value={settings.cacheTtlHours}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  cacheTtlHours: parseInt(e.target.value) || 24 
                })}
                min="1"
                max="168"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Максимальный размер кэша (МБ)</Label>
              <Input
                type="number"
                value={settings.maxCacheSizeMb}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  maxCacheSizeMb: parseInt(e.target.value) || 100 
                })}
                min="10"
                max="1000"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Автообновление</Label>
                <p className="text-xs text-muted-foreground">Автоматически обновлять кэш</p>
              </div>
              <Switch
                checked={settings.autoUpdate}
                onCheckedChange={(checked) => setSettings({ ...settings, autoUpdate: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Очистка старых данных</Label>
                <p className="text-xs text-muted-foreground">Удалять устаревшие записи</p>
              </div>
              <Switch
                checked={settings.cleanupOldData}
                onCheckedChange={(checked) => setSettings({ ...settings, cleanupOldData: checked })}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Административные уровни</Label>
              <div className="flex gap-2 flex-wrap">
                {[4, 5, 6, 7, 8].map(level => (
                  <label key={level} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={settings.adminLevels.includes(level)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSettings({
                            ...settings,
                            adminLevels: [...settings.adminLevels, level]
                          });
                        } else {
                          setSettings({
                            ...settings,
                            adminLevels: settings.adminLevels.filter(l => l !== level)
                          });
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-xs">Уровень {level}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSettingsOpen(false)}>
              Отмена
            </Button>
            <Button 
              onClick={handleSaveSettings}
              disabled={saveSettingsMutation.isPending}
            >
              {saveSettingsMutation.isPending ? "Сохранение..." : "Сохранить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}